import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, MapPin, Clock, Users, Calendar, Ticket, Share2, Trophy } from "lucide-react";
import api from "@/lib/api";

function FauxQR({ text }) {
  // deterministic grid from string hash
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) >>> 0;
  const cells = [];
  const size = 11;
  let seed = h;
  for (let i = 0; i < size * size; i++) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    cells.push(seed % 3 === 0);
  }
  return (
    <div className="grid bg-white p-2 rounded-lg" style={{ gridTemplateColumns: `repeat(${size}, 1fr)`, width: 132, height: 132 }}>
      {cells.map((on, i) => <div key={i} style={{ background: on ? "#0a0e17" : "#fff" }} />)}
    </div>
  );
}

export default function Confirmation() {
  const { ref } = useParams();
  const [b, setB] = useState(null);
  useEffect(() => { api.get(`/bookings/${ref}`).then((r) => setB(r.data)).catch(() => {}); }, [ref]);
  if (!b) return <div className="max-w-3xl mx-auto px-5 py-20 text-slate-400">Loading booking…</div>;

  const paid = b.payment_status === "paid";
  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: "Escape Room Express", text: `My booking ${b.booking_ref}`, url });
    else { navigator.clipboard.writeText(url); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-14">
      <div className="text-center mb-8 fade-up">
        <div className="h-16 w-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 grid place-items-center mx-auto glow-violet">
          <CheckCircle2 className="h-9 w-9 text-emerald-400" />
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl mt-4">Booking confirmed!</h1>
        <p className="text-slate-400 mt-2">A confirmation has been emailed to {b.customer_email}.</p>
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#241211] overflow-hidden" data-testid="confirmation-card">
        <div className="bg-gradient-to-r from-[#F9CF04] to-[#e0a500] p-6 flex items-center justify-between">
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-[#1a0a0a]/70">Digital Pass</div>
            <div className="font-display font-extrabold text-2xl text-[#1a0a0a]">{b.game_name}</div>
          </div>
          <Ticket className="h-8 w-8 text-[#1a0a0a]/80" />
        </div>
        <div className="p-6 flex flex-col sm:flex-row gap-6">
          <div className="flex flex-col items-center gap-2">
            <FauxQR text={b.booking_ref} />
            <span className="font-mono text-lg tracking-widest" data-testid="booking-ref">{b.booking_ref}</span>
          </div>
          <div className="flex-1 grid grid-cols-2 gap-4 text-sm">
            <Info icon={<MapPin className="h-4 w-4 text-[#F9CF04]" />} label="Location" value={b.location_name} />
            <Info icon={<Calendar className="h-4 w-4 text-emerald-400" />} label="Date" value={b.date} />
            <Info icon={<Clock className="h-4 w-4 text-amber-400" />} label="Time" value={`${b.start_time} – ${b.end_time}`} />
            <Info icon={<Users className="h-4 w-4 text-[#F9CF04]" />} label="Players" value={b.num_players} />
            <Info icon={<Ticket className="h-4 w-4 text-slate-400" />} label="Total" value={`₹${b.total_amount}`} />
            <Info label="Payment" value={<span className={paid ? "text-emerald-400" : "text-amber-400"}>{paid ? "Paid" : "Pay at venue"}</span>} />
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        <button onClick={share} data-testid="share-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 font-semibold transition-all">
          <Share2 className="h-4 w-4" /> Share
        </button>
        <Link to="/leaderboard" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold transition-all">
          <Trophy className="h-4 w-4" /> See leaderboard
        </Link>
        <Link to="/games" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#F9CF04] hover:bg-[#ffdb2e] text-[#1a0a0a] font-semibold transition-all">
          Book another
        </Link>
      </div>
    </div>
  );
}

const Info = ({ icon, label, value }) => (
  <div>
    <div className="text-xs text-slate-500 flex items-center gap-1">{icon}{label}</div>
    <div className="font-semibold mt-0.5">{value}</div>
  </div>
);
